import { Message, StreamChunk } from '../types'

/**
 * Utility class for processing server-sent event streams from the AI API.
 * Handles buffering, parsing, and extracting special content like edit proposals.
 */
export class StreamProcessor {
  private buffer = ''
  private decoder = new TextDecoder()

  /**
   * Processes a chunk of data from the stream.
   * Buffers incomplete lines and returns complete data lines.
   */
  processChunk(value: Uint8Array): string[] {
    // Decode the chunk and add to buffer
    this.buffer += this.decoder.decode(value, { stream: true })
    
    // Process complete lines
    const lines = this.buffer.split('\n')
    this.buffer = lines.pop() || '' // Keep incomplete line in buffer

    return lines.filter(line => line.startsWith('data: '))
  }

  /**
   * Parses a single data line from the stream into a StreamChunk object.
   * Returns null for [DONE] signals or parse errors.
   */
  parseStreamData(line: string): StreamChunk | null {
    const data = line.slice(6).trim()
    
    if (data === '[DONE]') {
      console.log('[StreamProcessor] Received [DONE] signal')
      return null
    }
    
    try {
      return JSON.parse(data)
    } catch (e) {
      console.log('[StreamProcessor] Failed to parse JSON:', data, e)
      return null
    }
  }

  /**
   * Extracts an edit proposal from the AI response content.
   * Edit proposals are marked with [EDIT_PROPOSAL] tags.
   */
  extractEditProposal(content: string): { original?: string; modified: string } | null {
    const editMatch = content.match(/\[EDIT_PROPOSAL\]([\s\S]*)\[\/EDIT_PROPOSAL\]/)
    if (!editMatch) return null

    let proposedContent = editMatch[1].trim()
    
    // Remove surrounding triple quotes if present (from AI response formatting)
    if (proposedContent.startsWith('"""') && proposedContent.endsWith('"""')) {
      proposedContent = proposedContent.slice(3, -3).trim()
    }
    
    return { modified: proposedContent }
  }

  /**
   * Removes edit proposal markup from the content for clean display.
   */
  cleanContentFromEditProposal(content: string): string {
    return content.replace(/\[EDIT_PROPOSAL\][\s\S]*\[\/EDIT_PROPOSAL\]/, '')
  }
}